import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  input,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { SentenceCasePipe } from '../../pipes/sentence.pipe';

@Component({
  selector: 'ag1-list-search',
  templateUrl: './ag1-list-search.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, AngularSvgIconModule, SentenceCasePipe],
})
export class Ag1ListSearchComponent implements OnInit {
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  /**
   * Required: items you want to allow users to search through
   */
  readonly items = input<any[]>([]);
  /**
   * Optional: keys you want the user to search through
   */
  readonly keys = input<string[]>([]);
  /**
   * Optional: placeholder for the search input
   */
  readonly placeholder = input<string>(undefined);

  /**
   * Required: used to tell parent what items the user's search resulted in
   * */
  @Output() filteredItemEvent = new EventEmitter<any[]>();

  /**
   * Optional: used to tell parent what the user's search term is
   */
  @Output() searchTermEvent = new EventEmitter<string>();
  searchTerm: string;

  constructor() {}

  ngOnInit(): void {
    if (!this.items()?.length) {
      console.error('Items are required for ag1-list-search component');
      return;
    }
  }

  get showSearchInterface(): boolean {
    // only show search interface if there are more than 4 items
    return this.items()?.length > 4;
  }

  filterItems(): void {
    const items = this.items();
    if (!items?.length) {
      this.filteredItemEvent.emit([]);
      this.searchTermEvent.emit('');
      return;
    }

    if (
      this.searchTerm === '' ||
      this.searchTerm === null ||
      this.searchTerm === undefined
    ) {
      this.filteredItemEvent.emit(items);
      this.searchTermEvent.emit('');
      return;
    }

    this.searchTermEvent.emit(this.searchTerm);

    // if keys are not provided, search through the items directly
    const keys = this.keys();
    if (!keys?.length) {
      this.filteredItemEvent.emit(
        items.filter((item: string) => {
          return this.compareItems(this.searchTerm, item);
        }),
      );
    }

    // if keys are provided, search through the items using the keys
    if (keys?.length) {
      this.filteredItemEvent.emit(
        items.filter((item: any) => {
          return this.keys().some((key) => {
            const itemValue = this.getNestedValue(item, key);
            return this.compareItems(this.searchTerm, itemValue);
          });
        }),
      );
    }
  }

  compareItems(searchTerm: string, item: any): boolean {
    const searchTerms: string[] = searchTerm
      .trim()
      .split(' ')
      .filter((term: string) => term.length);

    return searchTerms.some((term: string) =>
      `${item}`.toLowerCase().includes(term.toLowerCase().replace(/\s/g, '')),
    );
  }

  focusSearch(): void {
    if (this.showSearchInterface && this.searchInput?.nativeElement) {
      setTimeout(() => {
        this.searchInput.nativeElement.focus();
      });
    }
  }

  /**
   * Clears the search field and re-emits the full item list (same as an empty search).
   */
  clearSearch(): void {
    this.searchTerm = '';
    this.filterItems();
  }

  /**
   * Helper function to get nested property values using dot notation
   * @param obj - The object to extract the value from
   * @param path - The dot-notation path (e.g., 'foodBusinessOperator.name')
   * @returns The value at the specified path, or undefined if not found
   */
  private getNestedValue(obj: unknown, path: string): unknown {
    if (!obj || !path) {
      return undefined;
    }

    return path.split('.').reduce(
      (current, key) => {
        return current?.[key];
      },
      obj as Record<string, unknown>,
    );
  }
}
